// StepComponents: All step components for the Create Notice flow (form steps, review, etc.)
// Cleaned up for production use.
import React, { useMemo, useState, useRef } from "react";
import {
  FormGroup,
  Label,
  ErrorMessage,
  FormDescription,
  RadioContainer,
  RadioOption,
} from "./FormComponents";
import { formText } from "./FormText";
import { useAuth } from "../../context/AuthContext";
import { lightTheme } from "../../styles/theme";
import {
  Box,
  Typography,
  TextField,
  useTheme,
  useMediaQuery,
  Button,
  CircularProgress,
  IconButton,
  Dialog,
  Slide,
  Popover,
  Alert,
  Tooltip,
  Switch,
  FormControlLabel,
  InputAdornment,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import PhotoIcon from "@mui/icons-material/Photo";
import DriveImagePicker from "../Drive/DriveImagePicker";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import DescriptionIcon from "@mui/icons-material/Description";
import VideocamIcon from "@mui/icons-material/Videocam";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import { AttachmentStep } from "./FileUploadSteps";
import EventRsvpSettingCard from "./EventRsvpSettingCard";
import EventDateTimeFields from "./EventDateTimeFields";
import EventUrgentSettingCard from "./EventUrgentSettingCard";
import { composerEventDetailFieldSx } from "./composerBorderStyles";
import { useSnackbar } from "../../context/SnackbarContext";
import PlaceholderEditor from "./PlaceholderEditor";
import { NOTICE_VIDEO_INPUT_ACCEPT } from "../../constants/uploadLimits";
import { NoticeUploadProgress } from "../shared/NoticeUploadProgress";
import { NoticeComposerUploadedFileRow } from "./NoticeComposerUploadedFileRow";
import NoticeComposerAttachmentsCarousel from "./NoticeComposerAttachmentsCarousel";
import NoticeComposerAttachmentPicker from "./NoticeComposerAttachmentPicker";
import { isDriveVideoFile } from "../../utils/driveFolder";
import type { NoticeUploadProgressState } from "../../utils/presignedUpload";
const isComposerPreviewVideo = (
  preview: string | null,
  uploadedFiles?: StepProps["formData"]["uploadedFiles"]
) => {
  const first = uploadedFiles?.[0];
  return Boolean(
    preview && first && isDriveVideoFile(first.contentType, first.fileName)
  );
};

export interface StepProps {
  formData: {
    title: string;
    body: string;
    type: string;
    audience: string;
    attachmentUrl: string;
    uploadedFiles?: Array<{
      fileUrl: string;
      fileName: string;
      contentType: string;
      size: number;
    }>;
    eventStartDate?: Date | null;
    eventStartTime?: string;
    eventEndDate?: Date | null;
    eventEndTime?: string;
    eventLocation?: string;
    eventReminderMode?: string;
    eventReminderModes?: string[];
    eventRecurrenceFrequency?: string;
    eventRecurrenceEndMode?: "count" | "until";
    eventRecurrenceCount?: number;
    eventRecurrenceUntil?: Date | null;
    imageUrl?: string;
    eventBackgroundImage?: string;
    targetSubcircle?: string; // New field for targeted notices
    isUrgent?: boolean; // Toggle for making info notices urgent
    pollOptions?: Array<{ optionId: string; label: string; order: number }>;
    pollClosesAt?: string;
    pollMode?: "standard" | "claim";
    rsvpEnabled?: boolean;
    rsvpClosesAt?: string;
    publishMode?: "now" | "schedule";
    publishAt?: string;
    staffOnly?: boolean;
    driveFolderKey?: string;
  };
  errors: {
    title?: string;
    body?: string;
    type?: string;
    audience?: string;
    attachmentUrl?: string;
    eventStartDate?: string;
    eventStartTime?: string;
    eventEndDate?: string;
    eventEndTime?: string;
    eventLocation?: string;
    targetSubcircle?: string; // New field for targeted notices
    pollOptions?: string;
    rsvpClosesAt?: string;
    eventRecurrenceFrequency?: string;
    publishAt?: string;
  };
  handleChange: (
    e:
      | React.ChangeEvent<
          HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >
      | { target: { name: string; value: any } },
  ) => void;
  handleFileUpload?: (files: FileList) => Promise<void>;
  handleFileRemove?: (index: number) => void;
  handleAddAttachmentFromUrl?: (
    url: string,
    fileName?: string,
    contentType?: string,
  ) => void;
  setNoticeQualityScore?: (score: number) => void;
  isUploading?: boolean;
  uploadStatusMessage?: string;
  uploadProgress?: NoticeUploadProgressState | null;
  onCancelUpload?: () => void;
  noticeFlowId?: string;
}

const NoticeUploadPreviewBadge: React.FC<{
  isUploading?: boolean;
  uploadStatusMessage?: string;
  uploadProgress?: NoticeUploadProgressState | null;
  onCancelUpload?: () => void;
}> = ({
  isUploading,
  uploadStatusMessage,
  uploadProgress,
  onCancelUpload,
}) => {
  if (!isUploading) {
    return null;
  }

  return (
    <NoticeUploadProgress
      variant="overlay"
      progress={uploadProgress ?? null}
      statusMessage={uploadStatusMessage}
      onCancel={onCancelUpload}
    />
  );
};

export const TitleStep: React.FC<StepProps> = ({
  formData,
  errors,
  handleChange,
  handleFileUpload,
  handleFileRemove,
  handleAddAttachmentFromUrl,
  isUploading,
  uploadStatusMessage,
  uploadProgress,
  onCancelUpload,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [aiLoading, setAiLoading] = useState(false);
  const [originalTitle, setOriginalTitle] = useState(formData.title);
  const [hasChanges, setHasChanges] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [selectedDocument, setSelectedDocument] = useState<File | null>(null);
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isDriveOpen, setIsDriveOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const documentInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const plusButtonRef = useRef<HTMLButtonElement>(null);
  const { token } = useAuth();

  // Sync local preview/document with persisted uploadedFiles so attachments
  // re-appear when navigating back to this step.
  React.useEffect(() => {
    const firstFile = formData.uploadedFiles?.[0];

    if (!firstFile) {
      setPreview(null);
      setSelectedDocument(null);
      return;
    }

    const contentType = firstFile.contentType || "";
    if (
      contentType.startsWith("image/") ||
      isDriveVideoFile(contentType, firstFile.fileName)
    ) {
      setPreview(firstFile.fileUrl);
      setSelectedDocument(null);
    } else {
      setPreview(null);
      setSelectedDocument(null);
    }
  }, [formData.uploadedFiles]);

  const previewIsVideo = isComposerPreviewVideo(preview, formData.uploadedFiles);

  const handleAIRewrite = async () => {
    if (!formData.title.trim()) return;

    setAiLoading(true);
    try {
      // Import the AI utility dynamically to avoid build issues
      const { generateNoticeTitle } = await import("../../utils/aiUtils");
      const result = await generateNoticeTitle(formData.title, token);

      if (result.success && result.text) {
        setOriginalTitle(formData.title);
        handleChange({
          target: { name: "title", value: result.text },
        });
        setHasChanges(true);
      }
    } catch (error) {
      console.error("AI rewrite error:", error);
    } finally {
      setAiLoading(false);
    }
  };

  const handleRevert = () => {
    handleChange({
      target: { name: "title", value: originalTitle },
    });
    setHasChanges(false);
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      // Clear document if image is selected
      setSelectedDocument(null);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      if (handleFileUpload) await handleFileUpload(e.target.files);
      setShowAttachmentMenu(false);
    }
  };

  const handleDocumentChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedDocument(file);
      // Clear image preview if document is selected
      setPreview(null);
      if (handleFileUpload) await handleFileUpload(e.target.files);
      setShowAttachmentMenu(false);
      setAnchorEl(null);
    }
  };

  const handleVideoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedDocument(file);
      // Clear image preview if video is selected
      setPreview(null);
      if (handleFileUpload) await handleFileUpload(e.target.files);
      setShowAttachmentMenu(false);
      setAnchorEl(null);
    }
  };

  const handleAttachmentOption = (option: string) => {
    setShowAttachmentMenu(false);
    setAnchorEl(null);
    switch (option) {
      case "photos":
        fileInputRef.current?.click();
        break;
      case "camera":
        cameraInputRef.current?.click();
        break;
      case "document":
        documentInputRef.current?.click();
        break;
      case "video":
        videoInputRef.current?.click();
        break;
      case "drive":
        setIsDriveOpen(true);
        break;
    }
  };


  const handleDriveSelect = (
    url: string,
    fileName: string,
    contentType?: string,
  ) => {
    setSelectedDocument(null);
    handleAddAttachmentFromUrl?.(url, fileName, contentType);
    setIsDriveOpen(false);
  };

  const attachmentOptions = [
    { id: "drive", label: "Drive", icon: FolderOutlinedIcon, color: theme.palette.primary.main, disabled: !handleAddAttachmentFromUrl },
    {
      id: "photos",
      label: "Photos",
      icon: PhotoIcon,
      color: theme.palette.custom.navNoticeFeed,
      disabled: false,
    },
    {
      id: "camera",
      label: "Camera",
      icon: CameraAltIcon,
      color: lightTheme.colors.textStrong,
      disabled: false,
    },
    {
      id: "document",
      label: "Document",
      icon: DescriptionIcon,
      color: lightTheme.colors.materialBlue,
      disabled: false,
    },
    {
      id: "video",
      label: "Video",
      icon: VideocamIcon,
      color: lightTheme.colors.pinkStrong,
      disabled: false,
    },
  ];

  const handleRemoveImage = async () => {
    setPreview(null);
    setSelectedDocument(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    if (cameraInputRef.current) {
      cameraInputRef.current.value = "";
    }
    if (documentInputRef.current) {
      documentInputRef.current.value = "";
    }
    handleChange({
      target: { name: "attachmentUrl", value: "" },
    });
    if (handleFileUpload) {
      const emptyFileList = new DataTransfer().files;
      await handleFileUpload(emptyFileList);
    }
  };

  const attachmentDisplayFiles = useMemo(() => {
    if (formData.uploadedFiles?.length) {
      return formData.uploadedFiles;
    }
    if (preview) {
      return [
        {
          fileUrl: preview,
          fileName:
            selectedDocument?.name ||
            formData.uploadedFiles?.[0]?.fileName ||
            "Attachment",
          contentType: previewIsVideo
            ? "video/mp4"
            : selectedDocument?.type || "image/jpeg",
          size: selectedDocument?.size || 0,
        },
      ];
    }
    if (selectedDocument) {
      return [
        {
          fileUrl: "",
          fileName: selectedDocument.name,
          contentType: selectedDocument.type || "application/octet-stream",
          size: selectedDocument.size,
        },
      ];
    }
    return [];
  }, [
    formData.uploadedFiles,
    preview,
    previewIsVideo,
    selectedDocument,
  ]);

  const handleRemoveAttachment = async (index: number) => {
    if (formData.uploadedFiles?.length) {
      handleFileRemove?.(index);
      const willBeEmpty = formData.uploadedFiles.length <= 1;
      if (willBeEmpty) {
        setPreview(null);
        setSelectedDocument(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        if (cameraInputRef.current) cameraInputRef.current.value = "";
        if (documentInputRef.current) documentInputRef.current.value = "";
        handleChange({ target: { name: "attachmentUrl", value: "" } });
      }
      return;
    }
    await handleRemoveImage();
  };

  return (
    <FormGroup>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 400,
            color: "primary.main",
            fontSize: { xs: "1.125rem", sm: "1.25rem" },
          }}
        >
          {formText.labels.title}
        </Typography>
        <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleAIRewrite}
            disabled={aiLoading || !formData.title.trim()}
            startIcon={
              aiLoading ? (
                <CircularProgress size={16} />
              ) : (
                <AutoAwesomeIcon
                  sx={{ color: "common.white", fontSize: "18px" }}
                />
              )
            }
            sx={{
              border: "1px solid",
              borderColor: "primary.main",
              backgroundColor: "primary.main",
              color: "common.white",
              borderRadius: "8px",
              padding: isMobile ? "6px 12px" : "8px 16px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: isMobile ? "0.75rem" : "0.875rem",
              minWidth: isMobile ? "auto" : "140px",
              height: isMobile ? "32px" : "36px",
              boxShadow: `0 2px 4px ${lightTheme.colors.shadowDark}`,
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "primary.dark",
                borderColor: "primary.dark",
                boxShadow: `0 4px 8px ${lightTheme.colors.shadowBlack15}`,
                transform: "translateY(-1px)",
              },
              "&:disabled": {
                backgroundColor: "grey.300",
                borderColor: "grey.300",
                color: "grey.500",
                boxShadow: "none",
              },
            }}
          >
            {aiLoading
              ? "Generating..."
              : isMobile
                ? "Rewrite"
                : "Rewrite with Nova"}
          </Button>
          {hasChanges && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleRevert}
              sx={{
                border: "1px solid",
                borderColor: "grey.400",
                backgroundColor: "common.white",
                color: "grey.700",
                borderRadius: "8px",
                padding: "8px 16px",
                textTransform: "none",
                fontWeight: 500,
                fontSize: (theme) => theme.typography.body2.fontSize,
                minWidth: "80px",
                height: "36px",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "grey.50",
                  borderColor: "grey.500",
                },
              }}
            >
              Revert
            </Button>
          )}
        </Box>
      </Box>
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          mb: { xs: 1.5, sm: 2 },
          fontSize: { xs: "0.875rem", sm: "0.9375rem" },
        }}
      >
        {formText.descriptions.title}
      </Typography>

      {/* Title Input with Plus Button */}
      <Box
        sx={{
          display: "flex",
          gap: 1,
          alignItems: "flex-start",
          mb: { xs: 1.5, sm: 2 },
          position: "relative",
        }}
      >
        <PlaceholderEditor
          value={formData.title || ""}
          onChange={(value) =>
            handleChange({
              target: { name: "title", value },
            })
          }
          placeholder={formText.placeholders.title}
          error={!!errors.title}
          helperText={errors.title}
          inputProps={{ maxLength: 100 }}
          hideInputWhenHasPlaceholders
          sx={{
            "& .MuiOutlinedInput-root": {
              fontSize: { xs: "1rem", sm: "1rem" },
              padding: { xs: "4px 14px", sm: "7px 14px" },
              height: { xs: "36px", sm: "40px" },
            },
          }}
        />
        <input
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          ref={fileInputRef}
          onChange={handleImageChange}
        />
        <input
          type="file"
          accept="image/*"
          capture="environment"
          style={{ display: "none" }}
          ref={cameraInputRef}
          onChange={handleImageChange}
        />
        <input
          type="file"
          accept=".pdf,.doc,.docx,.txt,.rtf,.odt"
          style={{ display: "none" }}
          ref={documentInputRef}
          onChange={handleDocumentChange}
        />
        <input
          type="file"
          accept={NOTICE_VIDEO_INPUT_ACCEPT}
          style={{ display: "none" }}
          ref={videoInputRef}
          onChange={handleVideoChange}
        />
        <IconButton
          ref={plusButtonRef}
          disabled={isUploading}
          onClick={(e) => {
            if (isUploading) return;
            if (isMobile) {
              setShowAttachmentMenu(true);
            } else {
              setAnchorEl(e.currentTarget);
            }
          }}
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: "common.white",
            width: { xs: 36, sm: 40 },
            height: { xs: 36, sm: 40 },
            borderRadius: "50%",
            border: `2px solid ${theme.palette.primary.main}`,
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
              borderColor: theme.palette.primary.dark,
              transform: "scale(1.05)",
            },
            transition: "all 0.2s ease",
            flexShrink: 0,
            "&:disabled": {
              backgroundColor: "grey.300",
              borderColor: "grey.300",
              color: "grey.500",
              opacity: 0.75,
              cursor: "not-allowed",
            },
          }}
          aria-label="Add attachment"
        >
          <AddIcon />
        </IconButton>
      </Box>

      {/* Error message directly below attachment area */}
      {errors.attachmentUrl && (
        <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
          <Alert
            severity="warning"
            sx={{
              borderRadius: 2,
              boxShadow: `0 2px 8px ${lightTheme.colors.shadowDark}`,
              "& .MuiAlert-icon": {
                fontSize: { xs: "1.25rem", sm: "1.5rem" },
              },
              "& .MuiAlert-message": {
                fontSize: { xs: "0.875rem", sm: "0.9375rem" },
                fontWeight: 500,
              },
            }}
          >
            {errors.attachmentUrl}
          </Alert>
        </Box>
      )}

      {formData.type === "event" && (
        <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
          <EventUrgentSettingCard
            formData={formData}
            handleChange={handleChange}
          />
        </Box>
      )}

      {/* Show urgent toggle for info and direct notices */}
      {(formData.type === "info" || formData.type === "direct") && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mb: { xs: 1.5, sm: 2 },
          }}
        >
          <Tooltip title="Urgent notices notify parents via email and push notifications.">
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isUrgent || false}
                  onChange={(e) =>
                    handleChange({
                      target: { name: "isUrgent", value: e.target.checked },
                    })
                  }
                  color="error"
                  size={isMobile ? "small" : "medium"}
                />
              }
              label={
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    fontSize: "15px",
                  }}
                >
                  Mark as urgent notice
                </Typography>
              }
            />
          </Tooltip>
        </Box>
      )}

      {attachmentDisplayFiles.length > 0 ? (
        <NoticeComposerAttachmentsCarousel
          files={attachmentDisplayFiles}
          onRemove={handleRemoveAttachment}
          isUploading={isUploading}
          uploadStatusMessage={uploadStatusMessage}
          uploadProgress={uploadProgress}
          onCancelUpload={onCancelUpload}
        />
      ) : null}

      {/* Mobile Attachment Menu (WhatsApp-style bottom sheet) */}
      {isMobile && (
        <Dialog
          open={showAttachmentMenu}
          onClose={() => setShowAttachmentMenu(false)}
          TransitionComponent={Transition}
          fullScreen
          PaperProps={{
            sx: {
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              m: 0,
              maxHeight: "60vh",
              borderRadius: "24px 24px 0 0",
              backgroundColor: lightTheme.colors.grey100,
            },
          }}
          sx={{
            "& .MuiBackdrop-root": {
              backgroundColor: lightTheme.colors.blackOverlay50,
            },
          }}
        >
          <Box
            sx={{
              p: 3,
              pt: 2,
            }}
          >
            {/* Handle bar */}
            <Box
              sx={{
                width: 40,
                height: 4,
                backgroundColor: lightTheme.colors.materialGrey400,
                borderRadius: 2,
                mx: "auto",
                mb: 3,
              }}
            />

            {/* Attachment Options Grid */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: 3,
              }}
            >
              {attachmentOptions.map((option) => {
                const IconComponent = option.icon;
                return (
                  <Box
                    key={option.id}
                    onClick={() =>
                      !option.disabled && handleAttachmentOption(option.id)
                    }
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 1,
                      cursor: option.disabled ? "not-allowed" : "pointer",
                      opacity: option.disabled ? 0.5 : 1,
                      "&:active": {
                        transform: option.disabled ? "none" : "scale(0.95)",
                      },
                      transition: "transform 0.1s ease",
                    }}
                  >
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: "50%",
                        backgroundColor: "common.white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: `0 2px 8px ${lightTheme.colors.shadowDark}`,
                      }}
                    >
                      <IconComponent
                        sx={{
                          fontSize: 32,
                          color: option.color,
                        }}
                      />
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "0.75rem",
                        color: option.disabled
                          ? lightTheme.colors.grey500
                          : lightTheme.colors.textStrong,
                        textAlign: "center",
                        fontWeight: 500,
                      }}
                    >
                      {option.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Dialog>
      )}

      {/* Desktop Attachment Menu (Vertical list popup) */}
      {!isMobile && (
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          PaperProps={{
            sx: {
              minWidth: 240,
              maxWidth: 240,
              borderRadius: 2,
              backgroundColor: "common.white",
              boxShadow: `0 4px 20px ${lightTheme.colors.shadowBlack15}`,
              overflow: "hidden",
              py: 0.5,
              mt: -1,
            },
          }}
        >
          <Box>
            {attachmentOptions.map((option) => {
              const IconComponent = option.icon;
              return (
                <Box
                  key={option.id}
                  onClick={() => {
                    if (!option.disabled) {
                      handleAttachmentOption(option.id);
                      setAnchorEl(null);
                    }
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    px: 3,
                    py: 1.5,
                    cursor: option.disabled ? "not-allowed" : "pointer",
                    opacity: option.disabled ? 0.5 : 1,
                    "&:hover": {
                      backgroundColor: option.disabled
                        ? "transparent"
                        : lightTheme.colors.grey100,
                    },
                    transition: "background-color 0.2s ease",
                  }}
                >
                  <IconComponent
                    sx={{
                      fontSize: 24,
                      color: option.color,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.9375rem",
                      color: option.disabled
                        ? lightTheme.colors.grey500
                        : lightTheme.colors.grey900,
                      fontWeight: 400,
                    }}
                  >
                    {option.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Popover>
      )}

      {isDriveOpen && <DriveImagePicker onClose={() => setIsDriveOpen(false)} onSelect={file => handleDriveSelect(file.url, file.name.split("/").pop() || "image", file.type)} />}
    </FormGroup>
  );
};

// Transition for mobile attachment menu (slides up from bottom)
const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Combined Title and Image Step for Image Notices (WhatsApp-style)
const formatFileSizeForStep = (bytes: number) =>
  bytes >= 1024 * 1024
    ? (bytes / 1024 / 1024).toFixed(2) + " MB"
    : (bytes / 1024).toFixed(1) + " KB";

export const ImageTitleStep: React.FC<StepProps> = ({
  formData,
  errors,
  handleChange,
  handleFileUpload,
  handleFileRemove,
  handleAddAttachmentFromUrl,
  isUploading,
  uploadStatusMessage,
  uploadProgress,
  onCancelUpload,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [aiLoading, setAiLoading] = useState(false);
  const [originalTitle, setOriginalTitle] = useState(formData.title);
  const [hasChanges, setHasChanges] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [selectedDocument, setSelectedDocument] = useState<File | null>(null);
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isDriveOpen, setIsDriveOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const documentInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const plusButtonRef = useRef<HTMLButtonElement>(null);
  const { token } = useAuth();
  const { showMessage } = useSnackbar();

  // Sync local preview/document from persisted uploadedFiles so attachments
  // re-appear when navigating back to this step.
  React.useEffect(() => {
    const firstFile = formData.uploadedFiles?.[0];

    if (!firstFile) {
      setPreview(null);
      setSelectedDocument(null);
      return;
    }

    const contentType = firstFile.contentType || "";
    if (
      contentType.startsWith("image/") ||
      isDriveVideoFile(contentType, firstFile.fileName)
    ) {
      setPreview(firstFile.fileUrl);
      setSelectedDocument(null);
    } else {
      setPreview(null);
      setSelectedDocument(null);
    }
  }, [formData.uploadedFiles]);

  const previewIsVideo = isComposerPreviewVideo(preview, formData.uploadedFiles);

  const handleAIRewrite = async () => {
    if (!formData.title.trim()) return;

    setAiLoading(true);
    try {
      const { generateNoticeTitle } = await import("../../utils/aiUtils");
      const result = await generateNoticeTitle(formData.title, token);

      if (result.success && result.text) {
        setOriginalTitle(formData.title);
        handleChange({
          target: { name: "title", value: result.text },
        });
        setHasChanges(true);
      }
    } catch (error) {
      console.error("AI rewrite error:", error);
    } finally {
      setAiLoading(false);
    }
  };

  const handleRevert = () => {
    handleChange({
      target: { name: "title", value: originalTitle },
    });
    setHasChanges(false);
  };

  const fileCount = formData.uploadedFiles?.length || 0;
  const maxAttachments = 20;
  const canAddMore = fileCount < maxAttachments;

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      if (!canAddMore) {
        showMessage({
          message: `Maximum ${maxAttachments} attachments allowed. Remove a file first.`,
          severity: "warning",
        });
        e.target.value = "";
        return;
      }
      const file = e.target.files[0];
      setSelectedDocument(null);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      if (handleFileUpload) await handleFileUpload(e.target.files);
      setShowAttachmentMenu(false);
    }
  };

  const handleDocumentChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (e.target.files && e.target.files[0]) {
      if (!canAddMore) {
        showMessage({
          message: `Maximum ${maxAttachments} attachments allowed. Remove a file first.`,
          severity: "warning",
        });
        e.target.value = "";
        return;
      }
      const file = e.target.files[0];
      setSelectedDocument(file);
      setPreview(null);
      if (handleFileUpload) await handleFileUpload(e.target.files);
      setShowAttachmentMenu(false);
      setAnchorEl(null);
    }
  };

  const handleVideoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      if (!canAddMore) {
        showMessage({
          message: `Maximum ${maxAttachments} attachments allowed. Remove a file first.`,
          severity: "warning",
        });
        e.target.value = "";
        return;
      }
      const file = e.target.files[0];
      setSelectedDocument(file);
      setPreview(null);
      if (handleFileUpload) await handleFileUpload(e.target.files);
      setShowAttachmentMenu(false);
      setAnchorEl(null);
    }
  };

  const handleAttachmentOption = (optionId: string) => {
    setShowAttachmentMenu(false); // Close menu
    setAnchorEl(null); // Close Popover

    switch (optionId) {
      case "photos":
        fileInputRef.current?.click();
        break;
      case "camera":
        cameraInputRef.current?.click();
        break;
      case "document":
        documentInputRef.current?.click();
        break;
      case "video":
        videoInputRef.current?.click();
        break;
      case "drive":
        setIsDriveOpen(true);
        break;
      default:
        break;
    }
  };


  const handleDriveSelect = (
    url: string,
    fileName: string,
    contentType?: string,
  ) => {
    if (!canAddMore) {
      showMessage({
        message: `Maximum ${maxAttachments} attachments allowed. Remove a file first.`,
        severity: "warning",
      });
      setIsDriveOpen(false);
      setShowAttachmentMenu(false);
      return;
    }
    setSelectedDocument(null);
    handleAddAttachmentFromUrl?.(url, fileName, contentType);
    setIsDriveOpen(false);
    setShowAttachmentMenu(false);
  };

  const attachmentOptions = [
    { id: "drive", label: "Drive", icon: FolderOutlinedIcon, color: theme.palette.primary.main, disabled: !handleAddAttachmentFromUrl },
    {
      id: "photos",
      label: "Photos",
      icon: PhotoIcon,
      color: theme.palette.custom.navNoticeFeed,
      disabled: false,
    },
    {
      id: "camera",
      label: "Camera",
      icon: CameraAltIcon,
      color: lightTheme.colors.textStrong,
      disabled: false,
    },
    {
      id: "document",
      label: "Document",
      icon: DescriptionIcon,
      color: lightTheme.colors.materialBlue,
      disabled: false,
    },
    {
      id: "video",
      label: "Video",
      icon: VideocamIcon,
      color: lightTheme.colors.pinkStrong,
      disabled: false,
    },
  ];

  const handleRemoveImage = async () => {
    setPreview(null);
    setSelectedDocument(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    if (cameraInputRef.current) {
      cameraInputRef.current.value = "";
    }
    if (documentInputRef.current) {
      documentInputRef.current.value = "";
    }
    // Clear the attachment URL in form data
    handleChange({
      target: { name: "attachmentUrl", value: "" },
    });
    if (handleFileUpload) {
      // Trigger file removal by passing empty FileList
      const emptyFileList = new DataTransfer().files;
      await handleFileUpload(emptyFileList);
    }
  };

  return (
    <FormGroup>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 400,
          color: "primary.main",
          fontSize: { xs: "1.125rem", sm: "1.25rem" },
          mb: { xs: 1.5, sm: 1 },
        }}
      >
        {formText.labels.title}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          mb: { xs: 1.5, sm: 2 },
          fontSize: { xs: "0.875rem", sm: "0.9375rem" },
        }}
      >
        {formText.descriptions.title}
      </Typography>

      {/* Title Input with Plus Button */}
      <Box
        sx={{
          display: "flex",
          gap: 1,
          alignItems: "flex-start",
          mb: { xs: 1.5, sm: 2 },
          position: "relative",
        }}
      >
        <PlaceholderEditor
          value={formData.title || ""}
          onChange={(value) =>
            handleChange({
              target: { name: "title", value },
            })
          }
          placeholder={formText.placeholders.title}
          error={!!errors.title}
          helperText={errors.title}
          inputProps={{ maxLength: 100 }}
          hideInputWhenHasPlaceholders
          sx={{
            "& .MuiOutlinedInput-root": {
              fontSize: { xs: "1rem", sm: "1rem" },
              padding: { xs: "4px 14px", sm: "7px 14px" },
              height: { xs: "36px", sm: "40px" },
            },
          }}
        />
        <input
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          ref={fileInputRef}
          onChange={handleImageChange}
        />
        <input
          type="file"
          accept="image/*"
          capture="environment"
          style={{ display: "none" }}
          ref={cameraInputRef}
          onChange={handleImageChange}
        />
        <input
          type="file"
          accept=".pdf,.doc,.docx,.txt,.rtf,.odt"
          style={{ display: "none" }}
          ref={documentInputRef}
          onChange={handleDocumentChange}
        />
        <input
          type="file"
          accept={NOTICE_VIDEO_INPUT_ACCEPT}
          style={{ display: "none" }}
          ref={videoInputRef}
          onChange={handleVideoChange}
        />
        <IconButton
          ref={plusButtonRef}
          disabled={!canAddMore || isUploading}
          onClick={(e) => {
            if (!canAddMore || isUploading) return;
            if (isMobile) {
              setShowAttachmentMenu(true);
            } else {
              setAnchorEl(e.currentTarget);
            }
          }}
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: "common.white",
            width: { xs: 36, sm: 40 },
            height: { xs: 36, sm: 40 },
            borderRadius: "50%",
            border: `2px solid ${theme.palette.primary.main}`,
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
              borderColor: theme.palette.primary.dark,
              transform: "scale(1.05)",
            },
            "&:disabled": {
              backgroundColor: "grey.300",
              borderColor: "grey.300",
              color: "grey.500",
              opacity: 0.6,
              cursor: "not-allowed",
            },
            transition: "all 0.2s ease",
            flexShrink: 0,
          }}
          aria-label="Add attachment"
        >
          <AddIcon />
        </IconButton>
      </Box>

      {/* Uploaded Files - show all attachments (up to maxAttachments) */}
      {(selectedDocument ||
        (formData.uploadedFiles && formData.uploadedFiles.length > 0)) && (
        <Box sx={{ mt: 1.5, mb: 1.5 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Uploaded Files (
            {(formData.uploadedFiles?.length || 0) + (selectedDocument ? 1 : 0)}
            /{maxAttachments})
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {selectedDocument && (
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "grey.300",
                  backgroundColor: "grey.50",
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <DescriptionIcon
                  sx={{ fontSize: 40, color: lightTheme.colors.materialBlue }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 500,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {selectedDocument.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatFileSizeForStep(selectedDocument.size)}
                  </Typography>
                </Box>
                <IconButton
                  onClick={handleRemoveImage}
                  size="small"
                  sx={{
                    color: "text.secondary",
                    "&:hover": { backgroundColor: "grey.200" },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
            {formData.uploadedFiles?.map((file, index) => (
              <NoticeComposerUploadedFileRow
                key={`${file.fileUrl}-${index}`}
                file={file}
                index={index}
                onRemove={handleFileRemove}
                formatSize={formatFileSizeForStep}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Error message directly below attachment area */}
      {errors.attachmentUrl && (
        <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
          <Alert
            severity="warning"
            sx={{
              borderRadius: 2,
              boxShadow: `0 2px 8px ${lightTheme.colors.shadowDark}`,
              "& .MuiAlert-icon": {
                fontSize: { xs: "1.25rem", sm: "1.5rem" },
              },
              "& .MuiAlert-message": {
                fontSize: { xs: "0.875rem", sm: "0.9375rem" },
                fontWeight: 500,
              },
            }}
          >
            {errors.attachmentUrl}
          </Alert>
        </Box>
      )}

      {/* Rewrite Button Below Input */}
      <Box
        sx={{
          display: "flex",
          gap: 1,
          width: "100%",
          flexDirection: { xs: "column", sm: "row" },
          flexWrap: "wrap",
          alignItems: { xs: "stretch", sm: "center" },
          mb: { xs: 1.5, sm: 2 },
        }}
      >
        <Button
          variant="outlined"
          size="small"
          onClick={handleAIRewrite}
          disabled={aiLoading || !formData.title.trim()}
          startIcon={
            aiLoading ? (
              <CircularProgress size={16} />
            ) : (
              <AutoAwesomeIcon
                sx={{
                  color: "common.white",
                  fontSize: { xs: "16px", sm: "18px" },
                }}
              />
            )
          }
          sx={{
            border: "1px solid",
            borderColor: "primary.main",
            backgroundColor: "primary.main",
            color: "common.white",
            borderRadius: "8px",
            padding: { xs: "8px 12px", sm: "8px 16px" },
            textTransform: "none",
            fontWeight: 600,
            fontSize: { xs: "0.8125rem", sm: "0.875rem" },
            minWidth: { xs: "100%", sm: "140px" },
            height: { xs: "40px", sm: "36px" },
            boxShadow: `0 2px 4px ${lightTheme.colors.shadowDark}`,
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: "primary.dark",
              borderColor: "primary.dark",
              boxShadow: `0 4px 8px ${lightTheme.colors.shadowBlack15}`,
              transform: "translateY(-1px)",
            },
            "&:disabled": {
              backgroundColor: "grey.300",
              borderColor: "grey.300",
              color: "grey.500",
              boxShadow: "none",
            },
          }}
        >
          {aiLoading
            ? "Generating..."
            : isMobile
              ? "Rewrite"
              : "Rewrite with Nova"}
        </Button>
        {hasChanges && (
          <Button
            variant="outlined"
            size="small"
            onClick={handleRevert}
            sx={{
              border: "1px solid",
              borderColor: "grey.400",
              backgroundColor: "common.white",
              color: "grey.700",
              borderRadius: "8px",
              padding: { xs: "8px 12px", sm: "8px 16px" },
              textTransform: "none",
              fontWeight: 500,
              fontSize: { xs: "0.8125rem", sm: "0.875rem" },
              minWidth: { xs: "100%", sm: "80px" },
              height: { xs: "40px", sm: "36px" },
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "grey.50",
                borderColor: "grey.500",
              },
            }}
          >
            Revert
          </Button>
        )}
        {/* Show urgent toggle for image notices on same line */}
        {formData.type === "image" && (
          <Box
            sx={{
              ml: { xs: 0, sm: "auto" },
              mt: { xs: 0.5, sm: 0 },
              width: { xs: "100%", sm: "auto" },
              display: "flex",
              justifyContent: { xs: "flex-start", sm: "flex-end" },
            }}
          >
            <Tooltip title="Urgent notices notify parents via email and push notifications.">
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isUrgent || false}
                    onChange={(e) =>
                      handleChange({
                        target: { name: "isUrgent", value: e.target.checked },
                      })
                    }
                    color="error"
                    size={isMobile ? "small" : "medium"}
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      fontSize: "15px",
                    }}
                  >
                    Mark as urgent notice
                  </Typography>
                }
                sx={{
                  ml: { xs: 0, sm: 1 },
                }}
              />
            </Tooltip>
          </Box>
        )}
      </Box>

      {/* Image / video preview */}
      {preview && (
        <Box
          sx={{
            mt: 2,
            mb: 2,
            position: "relative",
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: `0 2px 8px ${lightTheme.colors.shadowDark}`,
            bgcolor: "grey.50",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {previewIsVideo ? (
            <Box
              component="video"
              src={preview}
              controls
              playsInline
              preload="metadata"
              sx={{
                display: "block",
                maxWidth: "100%",
                width: "auto",
                height: "auto",
                maxHeight: { xs: "min(52vh, 420px)", sm: "min(48vh, 440px)" },
                objectFit: "contain",
                bgcolor: "common.black",
              }}
            />
          ) : (
            <Box
              component="img"
              src={preview}
              alt="Notice preview"
              sx={{
                display: "block",
                maxWidth: "100%",
                width: "auto",
                height: "auto",
                maxHeight: { xs: "min(52vh, 420px)", sm: "min(48vh, 440px)" },
                objectFit: "contain",
              }}
            />
          )}
          <NoticeUploadPreviewBadge
            isUploading={isUploading}
            uploadStatusMessage={uploadStatusMessage}
            uploadProgress={uploadProgress}
            onCancelUpload={onCancelUpload}
          />
          <IconButton
            onClick={handleRemoveImage}
            disabled={isUploading}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              backgroundColor: lightTheme.colors.blackOverlay60,
              color: "common.white",
              "&:hover": {
                backgroundColor: lightTheme.colors.blackOverlay80,
              },
            }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      {/* Mobile Attachment Menu (WhatsApp-style bottom sheet) */}
      {isMobile && (
        <Dialog
          open={showAttachmentMenu}
          onClose={() => setShowAttachmentMenu(false)}
          TransitionComponent={Transition}
          fullScreen
          PaperProps={{
            sx: {
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              m: 0,
              maxHeight: "60vh",
              borderRadius: "24px 24px 0 0",
              backgroundColor: lightTheme.colors.grey100,
            },
          }}
          sx={{
            "& .MuiBackdrop-root": {
              backgroundColor: lightTheme.colors.blackOverlay50,
            },
          }}
        >
          <Box
            sx={{
              p: 3,
              pt: 2,
            }}
          >
            {/* Handle bar */}
            <Box
              sx={{
                width: 40,
                height: 4,
                backgroundColor: lightTheme.colors.materialGrey400,
                borderRadius: 2,
                mx: "auto",
                mb: 3,
              }}
            />

            {/* Attachment Options Grid */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: 3,
              }}
            >
              {attachmentOptions.map((option) => {
                const IconComponent = option.icon;
                return (
                  <Box
                    key={option.id}
                    onClick={() =>
                      !option.disabled && handleAttachmentOption(option.id)
                    }
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 1,
                      cursor: option.disabled ? "not-allowed" : "pointer",
                      opacity: option.disabled ? 0.5 : 1,
                      "&:active": {
                        transform: option.disabled ? "none" : "scale(0.95)",
                      },
                      transition: "transform 0.1s ease",
                    }}
                  >
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: "50%",
                        backgroundColor: "common.white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: `0 2px 8px ${lightTheme.colors.shadowDark}`,
                      }}
                    >
                      <IconComponent
                        sx={{
                          fontSize: 32,
                          color: option.color,
                        }}
                      />
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "0.75rem",
                        color: option.disabled
                          ? lightTheme.colors.grey500
                          : lightTheme.colors.textStrong,
                        textAlign: "center",
                        fontWeight: 500,
                      }}
                    >
                      {option.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Dialog>
      )}

      {/* Desktop Attachment Menu (Vertical list popup) */}
      {!isMobile && (
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          PaperProps={{
            sx: {
              minWidth: 240,
              maxWidth: 240,
              borderRadius: 2,
              backgroundColor: "common.white",
              boxShadow: `0 4px 20px ${lightTheme.colors.shadowBlack15}`,
              overflow: "hidden",
              py: 0.5,
              mt: -1,
            },
          }}
        >
          <Box>
            {attachmentOptions.map((option) => {
              const IconComponent = option.icon;
              return (
                <Box
                  key={option.id}
                  onClick={() => {
                    if (!option.disabled) {
                      handleAttachmentOption(option.id);
                      setAnchorEl(null);
                    }
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    px: 3,
                    py: 1.5,
                    cursor: option.disabled ? "not-allowed" : "pointer",
                    opacity: option.disabled ? 0.5 : 1,
                    "&:hover": {
                      backgroundColor: option.disabled
                        ? "transparent"
                        : lightTheme.colors.grey100,
                    },
                    transition: "background-color 0.2s ease",
                  }}
                >
                  <IconComponent
                    sx={{
                      fontSize: 24,
                      color: option.color,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.9375rem",
                      color: option.disabled
                        ? lightTheme.colors.grey500
                        : lightTheme.colors.grey900,
                      fontWeight: 400,
                    }}
                  >
                    {option.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Popover>
      )}

      {isDriveOpen && <DriveImagePicker onClose={() => setIsDriveOpen(false)} onSelect={file => handleDriveSelect(file.url, file.name.split("/").pop() || "image", file.type)} />}
    </FormGroup>
  );
};

export const ContentStep: React.FC<StepProps> = ({
  formData,
  errors,
  handleChange,
  handleFileUpload,
  handleFileRemove,
  handleAddAttachmentFromUrl,
  isUploading,
  uploadStatusMessage,
  uploadProgress,
  onCancelUpload,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { user, token } = useAuth();
  const schoolName = user?.schoolName as string;
  const [aiLoading, setAiLoading] = useState(false);
  const [originalContent, setOriginalContent] = useState(formData.body);
  const [hasChanges, setHasChanges] = useState(false);
  const attachmentFiles = formData.uploadedFiles || [];
  const allowAttachments = formData.type === "direct";

  const handleAIGenerate = async () => {
    setAiLoading(true);
    try {
      // Import the AI utility dynamically to avoid build issues
      const { generateNoticeContent } = await import("../../utils/aiUtils");

      // If there's existing content, use it as context. If not, just use the title
      const context = formData.body.trim()
        ? `Title: ${formData.title}\nCurrent Content: ${formData.body}`
        : `Title: ${formData.title}`;

      const result = await generateNoticeContent(
        formData.title || "Notice",
        context,
        token,
        schoolName,
      );

      if (result.success && result.text) {
        setOriginalContent(formData.body || "");
        handleChange({
          target: { name: "body", value: result.text },
        });
        setHasChanges(true);
      }
    } catch (error) {
      console.error("AI generation error:", error);
    } finally {
      setAiLoading(false);
    }
  };

  const handleRevert = () => {
    handleChange({
      target: { name: "body", value: originalContent },
    });
    setHasChanges(false);
  };
  return (
    <FormGroup>
      {attachmentFiles.length > 0 ? (
        <Box sx={{ mb: 2.5 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 400,
              color: "primary.main",
              fontSize: { xs: "1.125rem", sm: "1.25rem" },
              mb: 1,
            }}
          >
            Attachment
          </Typography>
          <NoticeComposerAttachmentsCarousel
            files={attachmentFiles}
            onRemove={handleFileRemove}
            isUploading={isUploading}
            uploadStatusMessage={uploadStatusMessage}
            uploadProgress={uploadProgress}
            onCancelUpload={onCancelUpload}
          />
        </Box>
      ) : null}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 400,
            color: "primary.main",
            fontSize: { xs: "1.125rem", sm: "1.25rem" },
          }}
        >
          {formText.labels.body}
          {formData.type === "event" && (
            <Box component="span" sx={{ color: "text.secondary", fontSize: "0.875rem", ml: 1 }}>
              (optional)
            </Box>
          )}
        </Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          {allowAttachments ? (
            <NoticeComposerAttachmentPicker
              disabled={isUploading}
              handleFileUpload={handleFileUpload}
              handleAddAttachmentFromUrl={handleAddAttachmentFromUrl}
            />
          ) : null}
          <Button
            variant="outlined"
            size="small"
            onClick={handleAIGenerate}
            disabled={aiLoading}
            startIcon={
              aiLoading ? (
                <CircularProgress size={16} />
              ) : (
                <AutoAwesomeIcon
                  sx={{ color: "common.white", fontSize: "18px" }}
                />
              )
            }
            sx={{
              border: "1px solid",
              borderColor: "primary.main",
              backgroundColor: "primary.main",
              color: "common.white",
              borderRadius: "8px",
              padding: isMobile ? "6px 12px" : "8px 16px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: isMobile ? "0.75rem" : "0.875rem",
              minWidth: isMobile ? "auto" : "140px",
              height: isMobile ? "32px" : "36px",
              boxShadow: `0 2px 4px ${lightTheme.colors.shadowDark}`,
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "primary.dark",
                borderColor: "primary.dark",
                boxShadow: `0 4px 8px ${lightTheme.colors.shadowBlack15}`,
                transform: "translateY(-1px)",
              },
              "&:disabled": {
                backgroundColor: "grey.300",
                borderColor: "grey.300",
                color: "grey.500",
                boxShadow: "none",
              },
            }}
          >
            {aiLoading ? "Generating..." : "Write with Nova"}
          </Button>
          {hasChanges && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleRevert}
              sx={{
                border: "1px solid",
                borderColor: "grey.400",
                backgroundColor: "common.white",
                color: "grey.700",
                borderRadius: "8px",
                padding: "8px 16px",
                textTransform: "none",
                fontWeight: 500,
                fontSize: (theme) => theme.typography.body2.fontSize,
                minWidth: "80px",
                height: "36px",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "grey.50",
                  borderColor: "grey.500",
                },
              }}
            >
              Revert
            </Button>
          )}
        </Box>
      </Box>

      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          mb: { xs: 1.5, sm: 2 },
          fontSize: { xs: "0.875rem", sm: "0.9375rem" },
        }}
      >
        {allowAttachments
          ? "Write the message and optionally attach photos, documents, or video."
          : formText.descriptions.body}
      </Typography>
      {errors.attachmentUrl && allowAttachments ? (
        <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
          <Alert
            severity="warning"
            sx={{
              borderRadius: 2,
              boxShadow: `0 2px 8px ${lightTheme.colors.shadowDark}`,
            }}
          >
            {errors.attachmentUrl}
          </Alert>
        </Box>
      ) : null}
      <PlaceholderEditor
        value={formData.body || ""}
        onChange={(value) =>
          handleChange({
            target: { name: "body", value },
          })
        }
        placeholder={formText.placeholders.body}
        error={!!errors.body}
        helperText={errors.body}
        multiline
        minRows={4}
        maxRows={8}
        inputProps={{ maxLength: 1000 }}
        sx={{ mb: 2 }}
        hideInputWhenHasPlaceholders
      />
    </FormGroup>
  );
};

export const DetailsStep: React.FC<StepProps> = ({
  formData,
  errors,
  handleChange,
}) => {
  return (
    <FormGroup>
      <Label>{formText.labels.type}</Label>
      <FormDescription>{formText.descriptions.type}</FormDescription>
      <RadioContainer>
        {formText.noticeTypes.map((type) => (
          <RadioOption
            key={type.id}
            isSelected={formData.type === type.id}
            onClick={() =>
              handleChange({
                target: {
                  name: "type",
                  value: type.id,
                },
              })
            }
          >
            <input
              type="radio"
              name="type"
              value={type.id}
              checked={formData.type === type.id}
              onChange={handleChange}
            />
            <div className="radio-circle" />
            <div className="label-text">{type.label}</div>
            <div className="description">{type.description}</div>
          </RadioOption>
        ))}
      </RadioContainer>
      {errors.type && <ErrorMessage>{errors.type}</ErrorMessage>}
    </FormGroup>
  );
};

export const EventInfoStep: React.FC<StepProps> = ({
  formData,
  errors,
  handleChange,
}) => {
  const eventDetailFieldSx = composerEventDetailFieldSx;

  return (
    <FormGroup style={{ marginBottom: 0 }}>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 400,
          color: "primary.main",
          fontSize: { xs: "1.125rem", sm: "1.25rem" },
          mb: { xs: 1.5, sm: 1 },
        }}
      >
        Event Details
      </Typography>

      <EventDateTimeFields
        startDate={formData.eventStartDate || null}
        startTime={formData.eventStartTime || ""}
        endDate={formData.eventEndDate || null}
        endTime={formData.eventEndTime || ""}
        errors={errors}
        onChange={(name, value) => handleChange({ target: { name, value } })}
      />

      <TextField
        fullWidth
        size="small"
        variant="outlined"
        name="eventLocation"
        required
        value={formData.eventLocation || ""}
        onChange={handleChange}
        placeholder="Enter event location"
        label="Location"
        error={!!errors.eventLocation}
        helperText={errors.eventLocation}
        sx={{ ...eventDetailFieldSx, mb: 1.5 }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <PlaceOutlinedIcon fontSize="small" color="action" />
            </InputAdornment>
          ),
        }}
      />

      <EventRsvpSettingCard
        formData={formData}
        errors={errors}
        handleChange={handleChange}
      />
    </FormGroup>
  );
};

export const CustomizationsStep: React.FC<StepProps> = (props) => {
  return (
    <>
      <AttachmentStep {...props} />
      {props.formData.type === "event" && <EventInfoStep {...props} />}
    </>
  );
};

// Image-Only Notice Step (no plus button, just empty state for images)
export const ImageOnlyTitleStep: React.FC<StepProps> = ({
  formData,
  errors,
  handleChange,
  handleFileUpload,
  handleAddAttachmentFromUrl,
  isUploading,
  uploadStatusMessage,
  uploadProgress,
  onCancelUpload,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { token } = useAuth();
  const [aiLoading, setAiLoading] = useState(false);
  const [originalTitle, setOriginalTitle] = useState(formData.title);
  const [hasChanges, setHasChanges] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isDriveOpen, setIsDriveOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // Sync preview from persisted uploadedFiles so image re-appears
  // when navigating back to this step for image/attachment notices.
  React.useEffect(() => {
    const firstFile = formData.uploadedFiles?.[0];

    if (!firstFile) {
      setPreview(null);
      return;
    }

    const contentType = firstFile.contentType || "";
    if (
      contentType.startsWith("image/") ||
      isDriveVideoFile(contentType, firstFile.fileName)
    ) {
      setPreview(firstFile.fileUrl);
    } else {
      setPreview(null);
    }
  }, [formData.uploadedFiles]);

  const previewIsVideo = isComposerPreviewVideo(preview, formData.uploadedFiles);

  const handleAIRewrite = async () => {
    if (!formData.title.trim()) return;

    setAiLoading(true);
    try {
      const { generateNoticeTitle } = await import("../../utils/aiUtils");
      const result = await generateNoticeTitle(formData.title, token);

      if (result.success && result.text) {
        setOriginalTitle(formData.title);
        handleChange({
          target: { name: "title", value: result.text },
        });
        setHasChanges(true);
      }
    } catch (error) {
      console.error("AI rewrite error:", error);
    } finally {
      setAiLoading(false);
    }
  };

  const handleRevert = () => {
    handleChange({
      target: { name: "title", value: originalTitle },
    });
    setHasChanges(false);
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      if (handleFileUpload) await handleFileUpload(e.target.files);
    }
  };

  const handleRemoveImage = async () => {
    setPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    if (cameraInputRef.current) {
      cameraInputRef.current.value = "";
    }
    handleChange({
      target: { name: "attachmentUrl", value: "" },
    });
    if (handleFileUpload) {
      const emptyFileList = new DataTransfer().files;
      await handleFileUpload(emptyFileList);
    }
  };

  const handleAttachmentOption = (optionId: string) => {
    setShowAttachmentMenu(false);
    setAnchorEl(null);

    switch (optionId) {
      case "photos":
        fileInputRef.current?.click();
        break;
      case "camera":
        cameraInputRef.current?.click();
        break;
      case "drive":
        setIsDriveOpen(true);
        break;
      default:
        break;
    }
  };



  const attachmentOptions = [
    { id: "drive", label: "Drive", icon: FolderOutlinedIcon, color: theme.palette.primary.main, disabled: !handleAddAttachmentFromUrl },
    {
      id: "photos",
      label: "Photos",
      icon: PhotoIcon,
      color: theme.palette.custom?.navNoticeFeed || theme.palette.primary.main,
      disabled: false,
    },
    {
      id: "camera",
      label: "Camera",
      icon: CameraAltIcon,
      color: lightTheme.colors.textStrong,
      disabled: false,
    },
  ];

  return (
    <FormGroup>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0,1fr)", md: "minmax(0,1.6fr) minmax(0,1fr)" }, gap: { xs: 2, md: 4 }, alignItems: "start" }}>
      <Box sx={{ minWidth: 0 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 400,
            color: "primary.main",
            fontSize: { xs: "1.125rem", sm: "1.25rem" },
          }}
        >
          {formText.labels.title}
        </Typography>
        <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleAIRewrite}
            disabled={aiLoading || !formData.title.trim()}
            startIcon={
              aiLoading ? (
                <CircularProgress size={16} />
              ) : (
                <AutoAwesomeIcon
                  sx={{ color: "common.white", fontSize: "18px" }}
                />
              )
            }
            sx={{
              border: "1px solid",
              borderColor: "primary.main",
              backgroundColor: "primary.main",
              color: "common.white",
              borderRadius: "8px",
              padding: isMobile ? "6px 12px" : "8px 16px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: isMobile ? "0.75rem" : "0.875rem",
              minWidth: isMobile ? "auto" : "140px",
              height: isMobile ? "32px" : "36px",
              boxShadow: `0 2px 4px ${lightTheme.colors.shadowDark}`,
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "primary.dark",
                borderColor: "primary.dark",
                boxShadow: `0 4px 8px ${lightTheme.colors.shadowBlack15}`,
                transform: "translateY(-1px)",
              },
              "&:disabled": {
                backgroundColor: "grey.300",
                borderColor: "grey.300",
                color: "grey.500",
                boxShadow: "none",
              },
            }}
          >
            {aiLoading
              ? "Generating..."
              : isMobile
                ? "Rewrite"
                : "Rewrite with Nova"}
          </Button>
          {hasChanges && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleRevert}
              sx={{
                border: "1px solid",
                borderColor: "grey.400",
                backgroundColor: "common.white",
                color: "grey.700",
                borderRadius: "8px",
                padding: "8px 16px",
                textTransform: "none",
                fontWeight: 500,
                fontSize: (theme) => theme.typography.body2.fontSize,
                minWidth: "80px",
                height: "36px",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "grey.50",
                  borderColor: "grey.500",
                },
              }}
            >
              Revert
            </Button>
          )}
        </Box>
      </Box>
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          mb: { xs: 1.5, sm: 2 },
          fontSize: { xs: "0.875rem", sm: "0.9375rem" },
        }}
      >
        {formText.descriptions.title}
      </Typography>

      {/* Title Input */}
      <TextField
        fullWidth
        variant="outlined"
        name="title"
        value={formData.title}
        onChange={handleChange}
        placeholder={formText.placeholders.title}
        error={!!errors.title}
        helperText={errors.title}
        sx={{
          mb: { xs: 1.5, sm: 2 },
          "& .MuiOutlinedInput-root": {
            fontSize: { xs: "1rem", sm: "1rem" },
            padding: { xs: "4px 14px", sm: "7px 14px" },
            height: { xs: "36px", sm: "40px" },
          },
        }}
        inputProps={{ maxLength: 100 }}
      />

      <TextField
        fullWidth
        multiline
        minRows={2}
        variant="outlined"
        name="body"
        value={formData.body}
        onChange={handleChange}
        placeholder="Add a short message parents will read with this image (optional)"
        error={!!errors.body}
        helperText={errors.body}
        sx={{ mb: { xs: 1.5, sm: 2 } }}
      />

      {(formData.type === "image" || formData.type === "image_notice") && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            mb: { xs: 1.5, sm: 2 },
          }}
        >
          <Tooltip title="Urgent notices notify parents via email and push notifications.">
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isUrgent || false}
                  onChange={(e) =>
                    handleChange({
                      target: { name: "isUrgent", value: e.target.checked },
                    })
                  }
                  color="error"
                  size={isMobile ? "small" : "medium"}
                />
              }
              label={
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    fontSize: "15px",
                  }}
                >
                  Mark as urgent notice
                </Typography>
              }
            />
          </Tooltip>
        </Box>
      )}

      </Box>
      <Box sx={{ minWidth: 0, pl: { xs: 0, md: 4 }, borderLeft: { xs: 0, md: "1px solid" }, borderLeftColor: { md: "divider" } }}>
        <Typography variant="h6" sx={{ color: "primary.main", fontWeight: 400, mb: .75 }}>Add an image</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Upload a photo or image to include in your notice.</Typography>
      {/* Hidden file inputs */}
      <input
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        ref={fileInputRef}
        onChange={handleImageChange}
      />
      <input
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: "none" }}
        ref={cameraInputRef}
        onChange={handleImageChange}
      />

      {/* Error message */}
      {errors.attachmentUrl && (
        <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
          <Alert
            severity="warning"
            sx={{
              borderRadius: 2,
              boxShadow: `0 2px 8px ${lightTheme.colors.shadowDark}`,
              "& .MuiAlert-icon": {
                fontSize: { xs: "1.25rem", sm: "1.5rem" },
              },
              "& .MuiAlert-message": {
                fontSize: { xs: "0.875rem", sm: "0.9375rem" },
                fontWeight: 500,
              },
            }}
          >
            {errors.attachmentUrl}
          </Alert>
        </Box>
      )}

      {/* Image Empty State or Preview */}

      {!preview ? (
        <Box
          onClick={(e) => {
            if (isUploading) return;
            if (isMobile) {
              fileInputRef.current?.click();
            } else {
              setAnchorEl(e.currentTarget);
            }
          }}
          sx={{
            mt: 1,
            mb: { xs: 2, sm: 1.5 },
            border: "2px dashed",
            borderColor: "grey.300",
            borderRadius: 2,
            p: { xs: 4, sm: 2.25 },
            minHeight: { md: 190 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            cursor: isUploading ? "wait" : "pointer",
            backgroundColor: "grey.50",
            transition: "all 0.2s ease",
            "&:hover": {
              borderColor: isUploading ? "grey.300" : "primary.main",
              backgroundColor: isUploading ? "grey.50" : "primary.50",
            },
            opacity: isUploading ? 0.85 : 1,
            position: "relative",
          }}
        >
          <NoticeUploadPreviewBadge
            isUploading={isUploading}
            uploadStatusMessage={uploadStatusMessage}
            uploadProgress={uploadProgress}
            onCancelUpload={onCancelUpload}
          />
          <PhotoIcon
            sx={{
              fontSize: { xs: 48, sm: 40 },
              color: "grey.400",
              mb: { xs: 2, sm: 1 },
            }}
          />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 500,
              color: "text.primary",
              mb: { xs: 1, sm: 0.5 },
              fontSize: { xs: "1rem", sm: "1rem" },
            }}
          >
            Add an image
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontSize: { xs: "0.875rem", sm: "0.875rem" },
            }}
          >
            Click to upload or drag and drop
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            mt: 2,
            mb: 2,
            position: "relative",
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: `0 2px 8px ${lightTheme.colors.shadowDark}`,
            bgcolor: "grey.50",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {previewIsVideo ? (
            <Box
              component="video"
              src={preview}
              controls
              playsInline
              preload="metadata"
              sx={{
                display: "block",
                maxWidth: "100%",
                width: "auto",
                height: "auto",
                maxHeight: { xs: "min(52vh, 420px)", sm: "min(48vh, 440px)" },
                objectFit: "contain",
                bgcolor: "common.black",
              }}
            />
          ) : (
            <Box
              component="img"
              src={preview}
              alt="Notice preview"
              sx={{
                display: "block",
                maxWidth: "100%",
                width: "auto",
                height: "auto",
                maxHeight: { xs: "min(52vh, 420px)", sm: "min(48vh, 440px)" },
                objectFit: "contain",
              }}
            />
          )}
          <NoticeUploadPreviewBadge
            isUploading={isUploading}
            uploadStatusMessage={uploadStatusMessage}
            uploadProgress={uploadProgress}
            onCancelUpload={onCancelUpload}
          />
          <IconButton
            onClick={handleRemoveImage}
            disabled={isUploading}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              backgroundColor: lightTheme.colors.blackOverlay60,
              color: "common.white",
              "&:hover": {
                backgroundColor: lightTheme.colors.blackOverlay80,
              },
            }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      </Box>
      </Box>
      {isDriveOpen && <DriveImagePicker onClose={() => setIsDriveOpen(false)} onSelect={file => { handleAddAttachmentFromUrl?.(file.url, file.name.split("/").pop() || "image", file.type); setPreview(file.url); setIsDriveOpen(false); }} />}
      {/* Mobile Attachment Menu */}
      {isMobile && (
        <Dialog
          open={showAttachmentMenu}
          onClose={() => setShowAttachmentMenu(false)}
          TransitionComponent={Transition}
          fullScreen
          PaperProps={{
            sx: {
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              m: 0,
              maxHeight: "60vh",
              borderRadius: "24px 24px 0 0",
              backgroundColor: lightTheme.colors.grey100,
            },
          }}
          sx={{
            "& .MuiBackdrop-root": {
              backgroundColor: lightTheme.colors.blackOverlay50,
            },
          }}
        >
          <Box sx={{ p: 3, pt: 2 }}>
            <Box
              sx={{
                width: 40,
                height: 4,
                backgroundColor: lightTheme.colors.materialGrey400,
                borderRadius: 2,
                mx: "auto",
                mb: 3,
              }}
            />
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: 3,
              }}
            >
              {attachmentOptions.map((option) => {
                const IconComponent = option.icon;
                return (
                  <Box
                    key={option.id}
                    onClick={() =>
                      !option.disabled && handleAttachmentOption(option.id)
                    }
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 1,
                      cursor: option.disabled ? "not-allowed" : "pointer",
                      opacity: option.disabled ? 0.5 : 1,
                      "&:active": {
                        transform: option.disabled ? "none" : "scale(0.95)",
                      },
                      transition: "transform 0.1s ease",
                    }}
                  >
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: "50%",
                        backgroundColor: "common.white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: `0 2px 8px ${lightTheme.colors.shadowDark}`,
                      }}
                    >
                      <IconComponent
                        sx={{
                          fontSize: 32,
                          color: option.color,
                        }}
                      />
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "0.75rem",
                        color: option.disabled
                          ? lightTheme.colors.grey500
                          : lightTheme.colors.textStrong,
                        textAlign: "center",
                        fontWeight: 500,
                      }}
                    >
                      {option.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Dialog>
      )}

      {/* Desktop Attachment Menu */}
      {!isMobile && (
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          PaperProps={{
            sx: {
              minWidth: 240,
              maxWidth: 240,
              borderRadius: 2,
              backgroundColor: "common.white",
              boxShadow: `0 4px 20px ${lightTheme.colors.shadowBlack15}`,
              overflow: "hidden",
              py: 0.5,
              mt: -1,
            },
          }}
        >
          <Box>
            {attachmentOptions.map((option) => {
              const IconComponent = option.icon;
              return (
                <Box
                  key={option.id}
                  onClick={() => {
                    if (!option.disabled) {
                      handleAttachmentOption(option.id);
                      setAnchorEl(null);
                    }
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    px: 3,
                    py: 1.5,
                    cursor: option.disabled ? "not-allowed" : "pointer",
                    opacity: option.disabled ? 0.5 : 1,
                    "&:hover": {
                      backgroundColor: option.disabled
                        ? "transparent"
                        : lightTheme.colors.grey100,
                    },
                    transition: "background-color 0.2s ease",
                  }}
                >
                  <IconComponent
                    sx={{
                      fontSize: 24,
                      color: option.color,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.9375rem",
                      color: option.disabled
                        ? lightTheme.colors.grey500
                        : lightTheme.colors.grey900,
                      fontWeight: 400,
                    }}
                  >
                    {option.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Popover>
      )}
    </FormGroup>
  );
};

export { ReviewStep } from "./ReviewStep";
export { AttachmentStep, ImageUploadStep } from "./FileUploadSteps";
